import React, { ChangeEvent, FC } from 'react';
import { makeStyles, withStyles } from '@material-ui/styles';
import { Box, Container, Grid, TextField, Theme, Typography } from '@material-ui/core';
import { FaqSearchIcon } from 'assets/svgs';
import { ProjectFaqItem } from 'services/cms/types';
import { observer } from 'mobx-react-lite';
import { Autocomplete } from '@material-ui/lab';
import commonStores from 'stores/common';
import { NextRouter, useRouter } from 'next/router';
import useI18n from "evcharging/hooks/useI18n";

export interface HeaderBgImageProps {
  title: string;
  text: string;
  backgroundSrc: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 520,
    padding: theme.spacing(10, 0),
    backgroundImage: ({ backgroundSrc }: Partial<HeaderBgImageProps>) => `url(${backgroundSrc})`,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(20, 0),
    },
  },
  textSettings: {
    whiteSpace: 'pre-line',
    textAlign: 'center',
    color: theme.palette.common.white,
  },
  link: {
    margin: theme.spacing(0, 'auto'),
  },
  search: {
    position: 'relative',
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.customColors.lightGray,
    margin: theme.spacing(8, 'auto', 0),
    maxWidth: 600,
    [theme.breakpoints.down('sm')]: {
      width: 335,
      marginTop: 64,
    },
    '& input': {
      marginLeft: theme.spacing(3.5),
    },
  },
  searchIcon: {
    padding: theme.spacing(0, 4),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputInput: {
    padding: theme.spacing(1.8, 1, 1.4),
    paddingLeft: `calc(1em + ${theme.spacing(4.5)}px)`,
    width: '100%',
    '& ::-webkit-input-placeholder': {
      /* WebKit browsers */ color: theme.palette.customColors.main,
      fontSize: 18,
      fontWeight: 400,
      opacity: 0.8,
    },
  },
}));

const StyledAutocomplete = withStyles((theme: Theme) => ({
  endAdornment: {
    '& .MuiAutocomplete-popupIndicator': {
      display: 'none',
    },
  },
  paper: {
    backgroundColor: theme.palette.common.white,
  },
  listbox: {
    padding: theme.spacing(1),
    '& li': {
      padding: theme.spacing(1.3, 1),
      borderRadius: 3,
    },
  },
}))(Autocomplete);

const HeaderBgImage: FC<HeaderBgImageProps> = observer((props) => {
  const { title, text, backgroundSrc } = props;
  const { t } = useI18n();
  const classes = useStyles({ backgroundSrc });
  const { faqStore } = commonStores;
  const { changeInputValue, inputValue, faqTitles, getProjectByQuestion, setIsOpen } = faqStore;
  const { push }: NextRouter = useRouter();

  const handleSelectChange = async (value?: string): Promise<void> => {
    changeInputValue(value || '');
    if (value) {
      const project = getProjectByQuestion(value);
      setIsOpen(project, true);
      const valueToId = value.split(' ').join('-');
      await push('/faq#' + valueToId);
    } else {
      await push('/faq');
    }
  };

  const handleChangeInputValue = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    changeInputValue(value);
  };

  return (
    <Box className={classes.root}>
      <Container maxWidth={false} fixed>
        <Grid container justifyContent="center">
          <Grid item lg={7} md={10} xs={12}>
            <Box>
              <Typography variant="h1" className={classes.textSettings}>
                {title}
              </Typography>
            </Box>
            <Box mt={3} mb={0}>
              <Typography variant="h6" component="h4" className={classes.textSettings}>
                {text}
              </Typography>
            </Box>

            <div className={classes.search}>
              <div className={classes.searchIcon}>
                <FaqSearchIcon />
              </div>
              <StyledAutocomplete
                clearOnEscape
                freeSolo
                options={faqTitles}
                onChange={({ target: { innerText } }: any) => handleSelectChange(innerText)}
                getOptionLabel={(option: ProjectFaqItem) => option.question}
                groupBy={(option: ProjectFaqItem) => option.project}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    value={inputValue}
                    onChange={handleChangeInputValue}
                    placeholder={t('faq.search')}
                    className={classes.inputInput}
                    inputProps={{ 'aria-label': 'search', ...params.inputProps }}
                    InputProps={{ disableUnderline: true, ...params.InputProps }}
                  />
                )}
              />
            </div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
});

export default HeaderBgImage;
